import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Icon, Input, Dropdown } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import ProfileApi from '~/api/services/profile'
import { FormFields } from '~/components/FormFields'
import { useForm } from 'react-hook-form'
import FileUpload from '~/components/FileUpload'
import { useAuth } from '~/routes/components/AuthProvider'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router-dom'
import { UsersQuery } from '~/QueryHooks/UsersQuery'
import UsersService from '~/api/services/users'
import { StyledImageContainer, StyledImageWrapper } from '~/pages/Users/User/Edit/style'
import { RouteTypes } from '~/routes/constants'
import { emailRegex } from '~/components/FormFields/constants'
import { FARMER_ROLE_SLUG } from '~/Constants'
import { handleErrors } from '~/utils/helpers'

const defaultImage = '/assets/default.png'

const CreateFarmerUser: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { systemData, systemData: { languages } } = useAuth()
  const { id } = useParams()
  const farmerRole = systemData?.roles.find(role => role.slug === 'user')

  const [correctEmail, setCorrectEmail] = useState(true)
  const [isBusy, setIsBusy] = useState(false)
  const [countriesCodes, setCountriesCodes] = useState([])

  const {
    isLoading: isBusyOptions,
    data: usersOptions,
    refetch: fetchUserOptions
  } = UsersQuery.getUserOptionsData()

  const { handleSubmit, control, setValue, watch } = useForm({
    mode: 'onSubmit'
  })

  const photoFieldUrl = watch('photo')
  const watchPhoneCodeField = watch('phonePrefix')
  const wathcMobileCodeField = watch('mobilePrefix')
  const mobileNumber = watch('mobileNumber')
  const phoneNumber = watch('phoneNumber')

  useEffect(() => {
    fetchProfileData()
  }, [])

  useEffect(() => {
    fetchUserOptions()
  }, [t])

  async function fetchProfileData() {
    setIsBusy(true)
    try {
      const options = await ProfileApi.getProfileOptions()
      const deMobilePrefix = options.data?.countries?.find(country => country?.iso === 'de')
      setValue('mobilePrefix', deMobilePrefix?.id)
      setCountriesCodes(options.data.countries.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso })))
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      await UsersService.storeUser({ ...data, farmerId: id, roleId: farmerRole.id })
      notify(t('Successfully created farmer user'), 'success')
      navigate(RouteTypes.DAIRY_FACTORY_FARMER_USERS)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderProfile() {
    return <>
      <Form.Field>
        {photoFieldUrl && <StyledImageWrapper>
          <img src={photoFieldUrl ? photoFieldUrl : defaultImage}></img>
          <StyledImageContainer />
          {photoFieldUrl && <Icon
            circular
            onClick={(e) => { e.preventDefault(); setValue('photo', null) }}
            name='trash'
            className='trashCan'
          />
          }
        </StyledImageWrapper>
        }
        {!photoFieldUrl && <FileUpload
          encodeFiles
          maxWidthDropZone={'100%'}
          onChange={(value) => setValue('photo', value)}
          accept={{
            'image/bmp': ['.bmp'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
          }}
          sizeLimitInMB={5}
        />
        }
      </Form.Field>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='users' style={{ fontSize: '1.2em' }}/>
            <div>{t('First name')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter first name')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'firstName'}
          placeholder={t('First name')}
        />
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='users' style={{ fontSize: '1.2em' }}/>
            <div>{t('Last name')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter last name')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'lastName'}
          placeholder={t('Last name')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='at' style={{ fontSize: '1.2em' }}/>
            <div>{t('Email')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter email')
            },
            pattern: {
              value: emailRegex,
              message: t('Please enter valid email')
            }
          }}
          typeInput={'emailField'}
          handleEmailCheck={(correctEmail) => setCorrectEmail(correctEmail)}
          style={{ marginBottom: 10 }}
          name={'email'}
          placeholder={t('Email')}
        />
        {languages.length > 1 && <FormFields.Select
          options={systemData?.languages.map(language => ({ value: language.id, text: language.label }))}
          selection
          selectOnBlur={false}
          placeholder={t('Select Language')}
          rules={{
            required: {
              value: true,
              message: t('Please select language')
            }
          }}
          control={control}
          style={{ marginBottom: 10 }}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'world'} style={{ fontSize: '1.2em' }}/>
            <div>{t('Language')}</div>
          </label>}
          name={'languageId'}
        />}
      </Form.Group>
      <Form.Group widths={'equal'}>
        <Form.Field style={{ marginBottom: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'text telephone'} style={{ fontSize: '1.2em' }}/>
            <div>{t('Phone number')}</div>
          </label>
          <Input
            style={{ minWidth: 250 }}
            value={phoneNumber}
            label={<Dropdown
              size='small'
              options={countriesCodes}
              selectOnBlur={false}
              basic
              scrolling
              clearable
              value={parseInt(watchPhoneCodeField) || ''}
              placeholder={t('Code')}
              onChange={(e, data) => setValue('phonePrefix', data.value)}
              name={'phonePrefix'}
            />}
            onChange={(e, data) => setValue('phoneNumber', data.value)}
            type={'number'}
            labelPosition='left'
            placeholder={watchPhoneCodeField ? t('Phone number') : t('Please select phone code')}
          />
        </Form.Field>
        <Form.Field style={{ paddingLeft: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'mobile alternate'} style={{ fontSize: '1.2em' }}/>
            <div>{t('Mobile number')}</div>
          </label>
          <Input
            style={{ minWidth: 250 }}
            value={mobileNumber}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={countriesCodes}
              selectOnBlur={false}
              onChange={(e, data) => setValue('mobilePrefix', data.value)}
              value={parseInt(wathcMobileCodeField) || ''}
              placeholder={t('Code')}
              name={'mobilePrefix'}
            />}
            onChange={(e, data) => setValue('mobileNumber', data.value)}
            labelPosition='left'
            placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
            type={'number'}
            name={'mobileNumber'}
          />
        </Form.Field>
      </Form.Group>
    </>
  }

  const role = usersOptions?.roles.find(role => role.slug === FARMER_ROLE_SLUG)?.label

  return <Layout
    segmented
    onSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy || isBusyOptions}
    disableSubmit={!correctEmail}
    action={t('Add')}
    title={t('{{role}} add', { role })}
  >
    {renderProfile()}
  </Layout>
}
export default CreateFarmerUser
